import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Lock, Ship, Clock, Layers } from "lucide-react";

const PRODUCT_INTERESTS = [
  "REM7 Smart Toilet",
  "REM7 Whirlpool Bathtubs",
  "Luxa / Aria Premium Toilets",
  "Aurelius / Aurelux Collection",
  "K2 / S1 / L1 / M3 Series",
  "Vena Basins & Vanities",
  "OEM / White-Label Program",
];

const TIMELINES = [
  "Within 1 month",
  "1–3 months",
  "3–6 months",
  "6–12 months",
  "12+ months / planning stage",
];

const ROLES = [
  "Hotel / Hospitality Group",
  "Developer / Builder",
  "Distributor / Showroom",
  "Architect / Designer",
  "Procurement / Purchasing",
  "Other",
];

const schema = z.object({
  fullName: z.string().trim().min(2, "Full name is required").max(100),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  company: z.string().trim().min(2, "Company name is required").max(150),
  role: z.string().min(1, "Please select your role"),
  timeline: z.string().min(1, "Please select a project timeline"),
  estimatedQuantity: z.string().trim().max(50).optional().or(z.literal("")),
  interests: z.array(z.string()).min(1, "Select at least one product interest"),
  message: z.string().trim().max(1500).optional().or(z.literal("")),
});

interface Props {
  onSuccess: () => void;
}

export const USAB2BInquiryForm = ({ onSuccess }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    timeline: "",
    estimatedQuantity: "",
    interests: [] as string[],
    message: "",
  });

  const toggleInterest = (item: string) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(item)
        ? f.interests.filter((i) => i !== item)
        : [...f.interests, item],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const first = Object.values(parsed.error.flatten().fieldErrors)[0]?.[0];
      toast.error(first ?? "Please complete the required fields");
      return;
    }
    setSubmitting(true);
    try {
      const composedMessage = [
        `Role: ${form.role}`,
        `Project timeline: ${form.timeline}`,
        form.estimatedQuantity ? `Estimated quantity: ${form.estimatedQuantity}` : null,
        `Product interests: ${form.interests.join(", ")}`,
        form.message ? `\nNotes:\n${form.message}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      const { error } = await supabase.from("leads").insert({
        name: form.fullName,
        email: form.email,
        phone: form.phone || null,
        company: form.company,
        location: "United States",
        interest_type: "usa_b2b_wholesale",
        message: composedMessage,
        qualification_score: 85,
        status: "new",
      });
      if (error) throw error;

      supabase.functions
        .invoke("send-lead-notification", {
          body: {
            name: form.fullName,
            email: form.email,
            phone: form.phone,
            company: form.company,
            country: "United States",
            inquiryType: "USA B2B Wholesale Inquiry",
            message: composedMessage,
          },
        })
        .catch(() => {});

      toast.success("Inquiry received. Delivery & logistics details unlocked below.");
      onSuccess();
    } catch (err) {
      toast.error("Submission failed. Please try again or email info@aureum7trading.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-8 lg:p-10 bg-gradient-to-br from-card to-card/60 border-primary/30 shadow-xl">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Lock className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-2xl font-bold mb-1">B2B Wholesale Inquiry</h3>
          <p className="text-sm text-muted-foreground">
            Submit your project details to unlock lead times, container logistics,
            and a tailored USD quote. Trade buyers only — MOQ 50 units.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="b2b-name">Full Name *</Label>
            <Input
              id="b2b-name"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              placeholder="Jane Smith"
              required
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="b2b-company">Company Name *</Label>
            <Input
              id="b2b-company"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="Acme Hospitality Group"
              required
              maxLength={150}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="b2b-email">Work Email *</Label>
            <Input
              id="b2b-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="info@aureum7trading.com"
              required
              maxLength={255}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="b2b-phone">Phone / WhatsApp</Label>
            <Input
              id="b2b-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 555 000 0000"
              maxLength={40}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Your Role *</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
            >
              <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Project Timeline *</Label>
            <Select
              value={form.timeline}
              onValueChange={(v) => setForm({ ...form, timeline: v })}
            >
              <SelectTrigger><SelectValue placeholder="When do you need delivery?" /></SelectTrigger>
              <SelectContent>
                {TIMELINES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="b2b-qty">Estimated Quantity (units)</Label>
          <Input
            id="b2b-qty"
            value={form.estimatedQuantity}
            onChange={(e) => setForm({ ...form, estimatedQuantity: e.target.value })}
            placeholder="e.g. 120 units"
            maxLength={50}
          />
        </div>

        <div className="space-y-3">
          <Label>Product Interests * (select all that apply)</Label>
          <div className="grid sm:grid-cols-2 gap-2">
            {PRODUCT_INTERESTS.map((item) => {
              const checked = form.interests.includes(item);
              return (
                <label
                  key={item}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border/60 hover:border-primary/40 cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleInterest(item)}
                  />
                  <span className="text-sm">{item}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="b2b-message">Project Notes (optional)</Label>
          <Textarea
            id="b2b-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Project location, scope, OEM requirements, target port of entry..."
            rows={4}
            maxLength={1500}
          />
        </div>

        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 font-medium text-foreground mb-2">
            <Lock className="w-4 h-4 text-primary" />
            Unlocked after submission
          </div>
          <ul className="space-y-1.5">
            <li className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-primary" /> Production & delivery lead times</li>
            <li className="flex items-center gap-2"><Ship className="w-3.5 h-3.5 text-primary" /> Freight options to your US port</li>
            <li className="flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-primary" /> Container loading & logistics plan</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Wholesale USD pricing within 48 hours</li>
          </ul>
        </div>

        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit B2B Inquiry & Unlock Delivery Details"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          By submitting you agree to be contacted by our B2B team regarding your project.
        </p>
      </form>
    </Card>
  );
};
